<template>
  <div class="chat-history-boundary">
    <button
      type="button"
      class="boundary-link"
      :style="{ color: color }"
      @click="handleClick"
    >
      {{ $t('session.chat.viewHistory') }}
    </button>
    <div class="boundary-divider">
      <span class="boundary-divider-text">
        {{ $t('session.chat.currentSessionMessages') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * ChatHistoryBoundary - 历史消息与本次会话消息之间的视觉边界块。
 * Purpose: 在 CustomerContainer 顶部的加载占位之后、消息列表渲染之前展示，
 *          上方为可点击的“点击查看历史消息”按钮，下方为带“本次会话消息”嵌字的横线。
 * Props: color (string, 品牌色，用于按钮文字颜色)。
 * Emits: load-history - 用户点击“点击查看历史消息”按钮时触发，由父组件接管真实加载逻辑。
 */
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'ChatHistoryBoundary',
  props: {
    color: {
      type: String,
      default: '#2e9b4a',
    },
  },
  emits: ['load-history'],
  setup(_, { emit }) {
    /** 点击按钮回调，仅向上抛出 load-history 事件，不持有任何业务状态。 */
    const handleClick = () => {
      emit('load-history')
    }

    return { handleClick }
  },
})
</script>

<style lang="less" scoped>
/**
 * 所有可变尺寸均通过 CSS 变量驱动，默认值等于组件最初的视觉规格，调用方可以按上下文覆盖。
 * - 宽度只用 max-width 约束横线视觉跨度，不锁死，保持 i18n / 响应式友好；
 * - 高度由内部元素自然撑出，不设 min-height；
 * - 按钮 / 嵌字宽度由 font-size × 文案字符数 自然撑出，避免多语言文本溢出或截断。
 */
.chat-history-boundary {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--boundary-gap, 12px);
  padding: var(--boundary-padding, 12px 0);
  width: 100%;
  max-width: var(--boundary-max-width, none);
  margin-inline: auto;
}

.boundary-link {
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-size: var(--boundary-button-font-size, 14px);
  line-height: var(--boundary-button-line-height, 22px);
  font-weight: 500;
  color: @primary-color;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.boundary-divider {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--boundary-divider-height, 20px);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    // 横线水平渐变：两侧浅、中间深；文字用背景色挡住中间一段，形成对称渐隐。
    background: linear-gradient(
      to right,
      rgba(242, 242, 242, 1) 0%,
      rgba(151, 156, 152, 1) 50%,
      rgba(242, 242, 242, 1) 100%
    );
  }
}

.boundary-divider-text {
  position: relative;
  padding: 0 12px;
  font-size: var(--boundary-divider-text-font-size, 12px);
  line-height: var(--boundary-divider-text-line-height, 20px);
  color: var(--text-3);
  background-color: @fill-disabled;
}
</style>
